import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Volume2, VolumeX } from "lucide-react";

export default function AudioPlayer() {
  const audioRef = useRef(null);
  const [entered, setEntered] = useState(false);
  const [muted, setMuted] = useState(false);

  // Lock page scroll while the intro gate is visible.
  useEffect(() => {
    document.body.style.overflow = entered ? "" : "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, [entered]);

  const enter = () => {
    const audio = audioRef.current;
    if (audio) {
      audio.volume = 0.6;
      audio.muted = false;
      audio.play().catch(() => {}); // user gesture -> allowed in all browsers
    }
    setEntered(true);
  };

  const toggle = () => {
    const audio = audioRef.current;
    if (!audio) return;
    const next = !muted;
    setMuted(next);
    audio.muted = next;
    if (!next) audio.play().catch(() => {});
  };

  return (
    <>
      <audio ref={audioRef} src="/music/paradise.mp3" loop preload="auto" />

      {/* Intro gate: the click guarantees audio starts with sound everywhere */}
      <AnimatePresence>
        {!entered && (
          <motion.div
            key="intro-gate"
            initial={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="fixed inset-0 z-[10000] flex flex-col items-center justify-center bg-pearl px-6 text-center"
            data-testid="intro-gate"
          >
            <div className="grain-overlay" aria-hidden="true" />

            <div className="absolute right-0 top-0 bottom-0 w-full md:w-1/2 h-full pointer-events-none z-0">
              <img
                src="/img/caraini.jpg"
                alt=""
                className="w-full h-full object-cover opacity-30"
              />
              <div className="absolute left-0 top-0 bottom-0 w-[25%] bg-gradient-to-r from-pearl to-transparent" />
            </div>

            <div className="relative z-10 flex flex-col items-center justify-center">
              <motion.p
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2, duration: 0.8 }}
                className="text-[11px] tracking-[0.35em] uppercase text-muted mb-8"
              >
                Kindermodel · Print · Laufsteg · Schauspielerin
              </motion.p>
              <motion.img
                src="/img/ini.png"
                alt="Janna Schwartz"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.35, duration: 0.9 }}
                className="w-full max-w-[650px] md:max-w-[650px] h-auto object-contain"
              />

              <motion.button
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6, duration: 0.8 }}
                onClick={enter}
                data-testid="intro-enter-button"
                className="group mt-14 inline-flex items-center gap-3 bg-charcoal text-pearl px-10 py-4 rounded-full text-xs tracking-[0.25em] uppercase transition-all duration-300 hover:bg-stone hover:scale-[1.02] active:scale-[0.98] hover:shadow-lg hover:shadow-charcoal/10"
              >
                <Volume2 className="w-4 h-4" strokeWidth={1.5} />
                Eintreten
                <span className="transition-transform duration-300 group-hover:translate-x-2">→</span>
              </motion.button>

              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.9, duration: 0.8 }}
                className="mt-6 text-[10px] tracking-[0.2em] uppercase text-muted"
              >
                Mit Musik ♪
              </motion.p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Floating mute/unmute control (after entering) */}
      {entered && (
        <motion.button
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          onClick={toggle}
          aria-label={muted ? "Musik einschalten" : "Musik stummschalten"}
          data-testid="music-toggle-button"
          className="fixed bottom-6 left-6 z-50 flex items-center gap-3 bg-charcoal/90 backdrop-blur-md text-pearl pl-4 pr-5 py-3 rounded-full text-[10px] tracking-[0.2em] uppercase hover:bg-charcoal transition-colors"
        >
          {muted ? (
            <VolumeX className="w-4 h-4" strokeWidth={1.5} />
          ) : (
            <span className="flex items-end gap-[2px] h-4" aria-hidden="true">
              {[0, 1, 2].map((i) => (
                <motion.span
                  key={i}
                  className="w-[2px] bg-pearl rounded-full"
                  animate={{ height: ["30%", "100%", "40%"] }}
                  transition={{ duration: 0.7, repeat: Infinity, delay: i * 0.18, ease: "easeInOut" }}
                  style={{ height: "60%" }}
                />
              ))}
            </span>
          )}
          {muted ? "Aus" : "An"}
          {!muted && <Volume2 className="w-4 h-4" strokeWidth={1.5} />}
        </motion.button>
      )}
    </>
  );
}
